/**
 * <variant-picker-cl> — option-value driven variant picker for the
 * combined-listings product template (sections/main-product-combined-listings.liquid).
 *
 * Mirrors the DOM side effects of VariantSelects (theme.js) without preloading
 * product.variants, so it works for products with more than 250 variants and
 * for Combined Listings child products (option values carrying data-product-url).
 *
 * Selection flow: collect checked option value ids -> fetch the section via the
 * Section Rendering API with ?option_values= -> swap targeted elements and the
 * picker itself -> dispatch variant:change with the selected variant parsed from
 * the response's embedded [data-selected-variant-cl] JSON.
 */
(function () {
  if (customElements.get('variant-picker-cl')) return;

  class VariantPickerCL extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.addEventListener('change', this.onOptionChange);
      this.normalizeInitialUrl();
    }

    // Remove ?option_values= parameter (Combined Listings) 
    normalizeInitialUrl() {
      if (!location.search.includes('option_values')) return;

      const variant = this.getSelectedVariant();
      if (!variant || !variant.id) return;

      const params = new URLSearchParams(location.search);
      params.delete('option_values');

      if (this.dataset.variantCount === '1') {
        params.delete('variant');
      } else {
        params.set('variant', variant.id);
      }

      const query = params.toString();
      const url = query ? `${location.pathname}?${query}` : location.pathname;
      history.replaceState({}, '', url);
    }

    get productForm() {
      return document.forms[this.getAttribute('form')];
    }

    getSelectedOptionValueIds() {
      return Array.from(this.querySelectorAll('fieldset'), (fieldset) => {
        return fieldset.querySelector('input[type="radio"]:checked')?.dataset.optionValueId;
      }).filter(Boolean);
    }

    onOptionChange(event) {
      const target = event.target;
      if (!target.matches('input[type="radio"][data-option-value-id]')) return;

      const optionValueIds = this.getSelectedOptionValueIds();

      // Combined listing: the selected value belongs to another child product.
      const productUrl = target.dataset.productUrl;
      if (productUrl && productUrl !== this.dataset.url) {
        this.switchProduct(productUrl, optionValueIds);
        return;
      }

      this.toggleBuyButton(true, '');
      this.removeErrorMessage();
      this.renderSection(optionValueIds);
    }

    switchProduct(productUrl, optionValueIds) {
      window.location.href = `${productUrl}?option_values=${optionValueIds.join(',')}`;
    }

    renderSection(optionValueIds) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      fetch(`${this.dataset.url}?option_values=${optionValueIds.join(',')}&section_id=${this.dataset.section}`, {
        signal: this.abortController.signal
      })
        .then((response) => response.text())
        .then((responseText) => {
          this.renderAll(responseText);
        })
        .catch((e) => {
          if (e.name !== 'AbortError') console.error(e);
        });
    }

    renderAll(responseText) {
      const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

      this.refreshPicker(parsedHTML);

      const variant = this.getSelectedVariant();

      if (!variant) {
        this.toggleBuyButton(true, '');
        this.setUnavailable();
        this.updatePickupAvailability(null);
        return;
      }

      this.updateElement(`Sku-${this.dataset.section}`, parsedHTML);
      this.updateElement(`Price-${this.dataset.section}`, parsedHTML);
      this.updateElement(`StickyPrice-${this.dataset.section}`, parsedHTML);
      this.updateElement(`Inventory-${this.dataset.section}`, parsedHTML);
      this.updateElement('attn-waitlist-eligibility', parsedHTML);

      this.updateVariantInput(variant);
      this.updateURL(variant);
      this.updatePickupAvailability(variant);
      this.toggleBuyButton(!variant.available, theme.variantStrings.soldOut);

      (this.productForm ?? document).dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: variant
        }
      }));

      document.dispatchEvent(new CustomEvent('productInfo:loaded'));
    }

    refreshPicker(parsedHTML) {
      const source = parsedHTML.getElementById(this.id);
      if (!source) return;

      const activeElement = document.activeElement;
      const focusedValueId = this.contains(activeElement) ? activeElement.dataset?.optionValueId : null;

      this.innerHTML = source.innerHTML;

      if (focusedValueId) {
        this.querySelector(`input[data-option-value-id="${focusedValueId}"]`)?.focus();
      }
    }

    getSelectedVariant() {
      const script = this.querySelector('[data-selected-variant-cl]');
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.error(e);
        return null;
      }
    }

    updateElement(id, parsedHTML) {
      const destination = document.getElementById(id);
      const source = parsedHTML.getElementById(id);

      if (source && destination) destination.innerHTML = source.innerHTML;
      if (destination) destination.classList.remove('invisible');
    }

    updateVariantInput(variant) {
      const productForms = document.querySelectorAll(`#ProductForm-${this.dataset.section}, #ProductFormInstallment-${this.dataset.section}`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateURL(variant) {
      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
    }

    updatePickupAvailability(variant) {
      const pickUpAvailability = document.querySelector('pickup-availability');
      if (!pickUpAvailability) return;

      if (variant) {
        pickUpAvailability.fetchAvailability(variant.id);
      } else {
        pickUpAvailability.setAttribute('hidden', '');
        pickUpAvailability.innerHTML = '';
      }
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;

      const productForm = section.querySelector('form[is="product-form"]');
      if (productForm) productForm.handleErrorMessage();
    }

    toggleBuyButton(disable = true, text) {
      const productForms = document.querySelectorAll(`#ProductForm-${this.dataset.section}, #StickyProductForm-${this.dataset.section}`);
      productForms.forEach((productForm) => {
        const buyButton = productForm.querySelector('[name="add"]');
        if (!buyButton) return;

        const buyButtonText = productForm.querySelector('[name="add"] > .btn-text');
        const buyButtonTextChild = productForm.querySelector('[name="add"] > .btn-text span');

        if (disable) {
          buyButton.setAttribute('disabled', '');
          if (text) {
            (buyButtonTextChild || buyButtonText).innerText = text;
          }
        } else {
          buyButton.removeAttribute('disabled');
          (buyButtonTextChild || buyButtonText).innerText = buyButton.dataset.preOrder === 'true' ? theme.variantStrings.preOrder : theme.variantStrings.addToCart;
        }
      });
    }

    setUnavailable() {
      const productForms = document.querySelectorAll(`#ProductForm-${this.dataset.section}, #StickyProductForm-${this.dataset.section}`);
      productForms.forEach((productForm) => {
        const buyButton = productForm.querySelector('[name="add"]');
        if (!buyButton) return;

        const buyButtonText = productForm.querySelector('[name="add"] > .btn-text');
        const buyButtonTextChild = productForm.querySelector('[name="add"] > .btn-text span');
        (buyButtonTextChild || buyButtonText).innerText = theme.variantStrings.unavailable;
      });

      const price = document.getElementById(`Price-${this.dataset.section}`);
      if (price) price.classList.add('invisible');

      const stickyPrice = document.getElementById(`StickyPrice-${this.dataset.section}`);
      if (stickyPrice) stickyPrice.classList.add('invisible');

      const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
      if (inventory) inventory.classList.add('invisible');
    }
  }

  customElements.define('variant-picker-cl', VariantPickerCL);
})();
